import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  { id: "care", label: "Website Care — CAD $25/mo" },
  { id: "careplus", label: "Website Care Plus — CAD $50/mo" },
  { id: "unsure", label: "Not sure yet — help me choose" },
];

const CarePlanModal = () => {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [plan, setPlan] = useState("careplus");
  const [form, setForm] = useState({ name: "", email: "", website: "", message: "" });

  const openModal = () => {
    setSent(false);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const chosen = PLANS.find((p) => p.id === plan);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name,
        email: form.email,
        business: form.website,
        phone: "",
        service_type: `Care plan signup — ${chosen.label}`,
        estimate: plan === "care" ? "CAD $25/mo" : plan === "careplus" ? "CAD $50/mo" : "",
        message: form.message || `Requesting the ${chosen.label} plan.`,
      });
      setSent(true);
      toast.success("Your care plan request is in.");
    } catch {
      toast.error("Something went wrong. Please email us directly at goodworkweb1@gmail.com.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E2DACB] px-4 py-3 text-sm text-[#233D35] placeholder-[#77786F]/60 focus:border-[#C58A47] focus:outline-none transition-colors";

  return (
    <>
      <button
        onClick={openModal}
        data-testid="care-plan-open"
        className="group inline-flex items-center gap-2 bg-[#C58A47] hover:bg-[#B0793A] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-6 py-3.5 transition-colors duration-200"
      >
        <ShieldCheck size={17} />
        Request a Care Plan
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="care-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] bg-[#1A2621]/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setOpen(false)}
            data-testid="care-modal-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              role="dialog"
              aria-modal="true"
              aria-label="Request a care plan"
              className="bg-white border border-[#E2DACB] w-full max-w-lg p-6 sm:p-8 relative shadow-[0_20px_60px_rgba(26,38,33,0.35)] max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="care-modal"
            >
              <button
                onClick={() => setOpen(false)}
                aria-label="Close care plan form"
                data-testid="care-modal-close"
                className="absolute top-4 right-4 w-10 h-10 inline-flex items-center justify-center text-[#77786F] hover:text-[#233D35] transition-colors"
              >
                <X size={22} />
              </button>

              {sent ? (
                <div className="py-10 text-center" data-testid="care-success">
                  <ShieldCheck size={36} className="mx-auto text-[#C58A47]" />
                  <span className="font-head font-bold uppercase tracking-tight text-2xl text-[#233D35] block mt-4">Request received.</span>
                  <p className="mt-3 text-sm text-[#77786F] leading-relaxed max-w-sm mx-auto">
                    We'll confirm compatibility and access for your site, then get your care plan started — nothing begins until we've agreed after launch.
                  </p>
                  <button onClick={() => setOpen(false)} data-testid="care-success-close"
                    className="mt-6 inline-flex items-center gap-2 border border-[#233D35] text-[#233D35] hover:bg-[#233D35] hover:text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-6 py-3 transition-colors">
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="care-form">
                  <span className="font-head font-bold uppercase tracking-[0.2em] text-xs text-[#C58A47]">Request a Care Plan</span>
                  <h2 className="font-head font-bold uppercase tracking-tight text-2xl text-[#233D35] mt-2">We'll take it from here.</h2>

                  <fieldset className="mt-5">
                    <legend className="text-sm font-semibold text-[#233D35] mb-2">Which plan?</legend>
                    <div className="space-y-2">
                      {PLANS.map((p) => (
                        <label key={p.id} data-testid={`care-plan-option-${p.id}`}
                          className={`flex items-center gap-3 p-3.5 border cursor-pointer transition-colors duration-200 ${
                            plan === p.id ? "border-[#C58A47] bg-[#C58A47]/5" : "border-[#E2DACB] hover:border-[#C58A47]"
                          }`}>
                          <input type="radio" name="care-plan" checked={plan === p.id} onChange={() => setPlan(p.id)} className="w-4 h-4 accent-[#C58A47]" />
                          <span className="text-sm text-[#233D35]">{p.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="mt-5 space-y-3">
                    <div>
                      <label htmlFor="c-name" className="block text-sm font-semibold text-[#233D35] mb-1.5">Name *</label>
                      <input id="c-name" required data-testid="care-name" className={inputCls} value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
                    </div>
                    <div>
                      <label htmlFor="c-email" className="block text-sm font-semibold text-[#233D35] mb-1.5">Email *</label>
                      <input id="c-email" type="email" required data-testid="care-email" className={inputCls} value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                    </div>
                    <div>
                      <label htmlFor="c-website" className="block text-sm font-semibold text-[#233D35] mb-1.5">Your website</label>
                      <input id="c-website" data-testid="care-website" className={inputCls} value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="yourbusiness.ca" />
                    </div>
                    <div>
                      <label htmlFor="c-message" className="block text-sm font-semibold text-[#233D35] mb-1.5">Anything we should know?</label>
                      <textarea id="c-message" rows={2} data-testid="care-message" className={inputCls} value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Optional" />
                    </div>
                  </div>

                  <button type="submit" disabled={sending} data-testid="care-submit"
                    className="group mt-5 inline-flex items-center gap-2 bg-[#C58A47] hover:bg-[#B0793A] disabled:opacity-60 text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-6 py-3 transition-colors duration-200">
                    {sending ? "Sending…" : "Send Request"}
                    <Send size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CarePlanModal;
